import { applyAntiforgeryHeader, isSafeMethod } from '../antiforgery.js';
import { PROBLEM_CONTENT_TYPE, isApiMessageArray, isBedrockProblem, toBedrockProblem } from './contracts.js';
import { NetworkError, ProblemError, UnexpectedResponseError } from './errors.js';
const JSON_CONTENT_TYPE = 'application/json';
/** A body that declared a media type but could not be parsed as JSON. */
const UNPARSEABLE = Symbol('unparseable');
function mediaType(response) {
    return (response.headers.get('content-type') ?? '').split(';', 1)[0].trim().toLowerCase();
}
function isJsonObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/**
 * Resolves the caller's input against the page it was called from.
 *
 * A relative string and an absolute URL have to classify identically for the same-origin decision
 * below, and a relative string alone carries no origin to compare.
 *
 * The type check is not redundant with the signature. TypeScript is erased, so an untyped caller —
 * or a `Request` handed over through JSON, a cast, or a JavaScript call site — would otherwise be
 * stringified to `[object Request]`, which resolves against the base as a perfectly valid relative
 * path and sends the request somewhere nobody asked for.
 */
function resolve(input) {
    if (typeof input === 'string') {
        return new URL(input, document.baseURI);
    }
    if (input instanceof URL) {
        return input;
    }
    throw new TypeError('A Bedrock request takes a string or a URL; a Request object is not accepted.');
}
/**
 * Builds the request headers: the caller's first, the antiforgery header last.
 *
 * The order is the contract (ADR-017 D5). Applying the token after the caller's headers is what
 * makes it non-overridable on a same-origin unsafe request.
 */
function buildHeaders(url, init) {
    const headers = new Headers(init?.headers);
    const method = (init?.method ?? 'GET').toUpperCase();
    if (url.origin === location.origin && !isSafeMethod(method)) {
        applyAntiforgeryHeader(headers);
    }
    return headers;
}
/**
 * Issues the request, separating "no response" from "the caller changed its mind".
 *
 * Cancellation rethrows whatever the platform threw, unchanged, so it stays cancellation all the
 * way up to the caller that asked for it.
 *
 * Credentials are pinned to `same-origin` after the caller's `init` is spread, for the same reason
 * and in the same position as the antiforgery header: that cookie is what the header is validated
 * against, so a caller-supplied `credentials: 'omit'` would send a token the server has nothing to
 * compare it to and turn a valid request into a security refusal. It is also the platform default,
 * so this costs a caller that never set it nothing.
 */
async function send(url, init, headers) {
    try {
        return await fetch(url, { ...init, headers, credentials: 'same-origin' });
    }
    catch (cause) {
        if (init?.signal?.aborted === true || (cause instanceof Error && cause.name === 'AbortError')) {
            throw cause;
        }
        throw new NetworkError(url.href, cause);
    }
}
/**
 * Parses a body the response declared as `expected`.
 *
 * Returns {@link UNPARSEABLE} when the media type matches but the body does not parse, and null
 * when the media type is something else entirely. The two are different failures and the callers
 * classify them differently.
 */
async function readJson(response, expected) {
    if (mediaType(response) !== expected) {
        return null;
    }
    try {
        return await response.json();
    }
    catch {
        return UNPARSEABLE;
    }
}
function notBedrock(response, url) {
    throw new UnexpectedResponseError('not-a-bedrock-response', response.status, response.headers.get('content-type'), url.href);
}
function mismatch(response, url) {
    throw new UnexpectedResponseError('envelope-mismatch', response.status, response.headers.get('content-type'), url.href);
}
/**
 * Turns a non-success response into the failure it actually is.
 *
 * A valid Bedrock problem document becomes a refusal the caller can key on. Everything else —
 * wrong media type, unparseable body, a `ProblemDetails` that never passed the Bedrock boundary
 * and so carries no `code` — is a response from outside the contract.
 */
async function refuse(response, url) {
    const body = await readJson(response, PROBLEM_CONTENT_TYPE);
    // Both checks earn their place, in this order: `isJsonObject` narrows the parsed body to an
    // indexable record, and `isBedrockProblem` then decides whether that record is the contract.
    // Reversing them compiles to a value the reader cannot index.
    if (isJsonObject(body) && isBedrockProblem(body)) {
        throw new ProblemError(toBedrockProblem(body, response.status));
    }
    return notBedrock(response, url);
}
/**
 * Reads a successful body that must be the Bedrock success envelope.
 *
 * A 2xx that is not JSON at all is not a Bedrock answer — a followed login redirect and an SPA
 * fallback both land here as `200 text/html`, and neither is the caller having chosen the wrong
 * operation. A 2xx that *is* JSON but carries the wrong envelope is exactly that caller mistake,
 * which is the distinction the two reasons exist to keep.
 */
async function readSuccessEnvelope(response, url) {
    const body = await readJson(response, JSON_CONTENT_TYPE);
    if (body === null || body === UNPARSEABLE || !isJsonObject(body)) {
        return notBedrock(response, url);
    }
    return body;
}
/**
 * Calls a valued Bedrock endpoint — one whose handler returns `Result&lt;T&gt;`.
 *
 * A success is always `200` carrying `{ data, messages }`; the envelope does not depend on whether
 * messages exist, so a `204` here means the endpoint is valueless and the caller wanted
 * {@link fetchCommand}.
 *
 * The envelope is validated structurally. `T` is **not** validated at runtime — it is a
 * compile-time assertion about `data`, and a caller that needs more narrows it itself.
 *
 * @throws {ProblemError} the boundary refused the request.
 * @throws {UnexpectedResponseError} the response is outside the contract, or is a success in the
 * wrong envelope.
 * @throws {NetworkError} the request produced no response.
 * @throws {AntiforgeryTokenMissingError} an unsafe same-origin call on a page with no token.
 */
export async function fetchResult(input, init) {
    const url = resolve(input);
    const response = await send(url, init, buildHeaders(url, init));
    if (!response.ok) {
        return await refuse(response, url);
    }
    // 204 is a successful valueless answer, which this operation cannot represent. The server was
    // right and the caller picked the wrong operation, which is what the reason records.
    if (response.status === 204) {
        return mismatch(response, url);
    }
    const body = await readSuccessEnvelope(response, url);
    if (body['data'] === undefined || !isApiMessageArray(body['messages'])) {
        return mismatch(response, url);
    }
    return {
        data: body['data'],
        messages: body['messages'],
    };
}
/**
 * Calls a valueless Bedrock endpoint — one whose handler returns `Result`.
 *
 * `204` is the ordinary success and normalizes to an empty list; a success with something to say
 * arrives as `200` with `{ messages }` and no `data`.
 *
 * @throws {ProblemError} the boundary refused the request.
 * @throws {UnexpectedResponseError} the response is outside the contract, or is a success in the
 * wrong envelope.
 * @throws {NetworkError} the request produced no response.
 * @throws {AntiforgeryTokenMissingError} an unsafe same-origin call on a page with no token.
 */
export async function fetchCommand(input, init) {
    const url = resolve(input);
    const response = await send(url, init, buildHeaders(url, init));
    if (!response.ok) {
        return await refuse(response, url);
    }
    if (response.status === 204) {
        return [];
    }
    const body = await readSuccessEnvelope(response, url);
    // A `data` member means the endpoint is valued and the caller wanted fetchResult; the valueless
    // envelope carries messages and nothing else.
    if (body['data'] !== undefined || !isApiMessageArray(body['messages'])) {
        return mismatch(response, url);
    }
    return body['messages'];
}
//# sourceMappingURL=fetch.js.map